import { ReactComponent as OutlineEdit1 } from "assets/svg/outline-edit-1.svg";
import { ReactComponent as OutlineInfoCircle16 } from "assets/svg/outline-info-circle-16.svg";
import ActionsDropdown from "components/actions-dropdown/ActionsDropdown";
import Checkbox from "components/checkbox/Checkbox";
import CustomSelect from "components/custom-select/CustomSelect";
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import ErrorMessage from "components/error-message";
import { PRACTICE_SETTING } from "components/provider-intake/utility/providerInfo";
import RadioButton from "components/radio-button";
import SuccessMessage from "components/success-message";
import FormControlWithMask from "components/utility/FormControlWithMask";
import { PHONE_NUMBER_MASK } from "constants/phoneNumberMask";
import { STATE_OPTIONS } from "constants/stateOptions";
import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Form,
  OverlayTrigger,
  Table,
  Tooltip,
  Row,
} from "react-bootstrap";
import postOrchidPro from "services/postOrchidPro";
import { track } from "utils/amplitude";
import { filter_keys } from "utils/fn";
import scrollToInvalidInput from "utils/scrollToInvalidInput";
import {
  ORG_ENTITY_TYPE_GROUP,
  ORG_ENTITY_TYPE_SOLO,
} from "../utility/constants";
import {
  IN_PERSON_POS_OPTIONS,
  PRACTICE_SETTING_RADIOS,
  TELEHEALTH_POS_OPTIONS,
} from "./constants";
import ImageUpload from "./ImageUpload";
import OfficeLocationEditor from "./OfficeLocationEditor";

import IconButton from "components/icon-button";
import getClasses from "utils/getClasses";
import styles from "./PracticeDetails.module.scss";
import BillingAddressEditor from "./BillingAddressEditor";

export type TelehealthOfficeLocation = {
  office_location_id: string;
  office_name: string;
  video_link?: string;
  session_location_type: string;
  office_address_line1: string;
  office_address_line2?: string;
  office_city: string;
  office_state: string;
  office_zipcode: string;
  phone_number?: string;
  email_address?: string;
  telehealth_pos_code: string;
  telehealth_modifier: string;
  show_address_to_public: boolean;
  is_default: boolean;
};

export type InPersonOfficeLocation = {
  office_location_id: string;
  office_name: string;
  session_location_type: string;
  office_address_line1: string;
  office_address_line2?: string;
  office_city: string;
  office_state: string;
  office_zipcode: string;
  phone_number?: string;
  email_address?: string;
  in_person_pos_code: string;
  in_person_modifier: string | null;
  show_address_to_public: boolean;
  is_default: boolean;
};

export type OfficeLocation = TelehealthOfficeLocation | InPersonOfficeLocation;

const PracticeDetails = ({ amplitudePageName }) => {
  const { orchidPro } = useOrchidPro();
  const [data, setData] = useState<Partial<OrchidPro>>({});
  useEffect(() => {
    if (!orchidPro) {
      return;
    }
    setData(filter_keys(orchidPro, PRACTICE_SETTING));
  }, [orchidPro]);

  const [telehealthLocations, setTelehealthLocations] = useState<
    TelehealthOfficeLocation[]
  >([]);
  const [inPersonLocations, setInPersonLocations] = useState<
    InPersonOfficeLocation[]
  >([]);

  useEffect(() => {
    if (!data) return;
    if (
      data.additional_office_locations &&
      data.additional_office_locations.length === 0
    ) {
      const telehealthLocation = [
        {
          office_location_id: "telehealth-0",
          office_name: "Orchid Telehealth",
          session_location_type: "telehealth",
          video_link: "Telehealth",
          office_address_line1: data?.office_address_line1,
          office_address_line2: data?.office_address_line2,
          office_city: data?.office_city,
          office_state: data?.office_state,
          office_zipcode: data?.office_zipcode,
          // phone_number: "",
          // email_address: "",
          telehealth_pos_code: data.telehealth_pos_code ?? "10",
          telehealth_modifier: data.telehealth_modifier ?? "95",
          show_address_to_public: data.show_address_to_public ?? false,
          is_default: true,
        },
      ];
      const inPersonLocation = [
        {
          office_location_id: "in-person-0",
          office_name: "-",
          session_location_type: "in-person",
          office_address_line1: data?.office_address_line1,
          office_address_line2: data?.office_address_line2,
          office_city: data?.office_city,
          office_state: data?.office_state,
          office_zipcode: data?.office_zipcode,
          // phone_number: "",
          // email_address: "",
          in_person_pos_code: data.in_person_pos_code ?? "11",
          in_person_modifier: data.in_person_modifier ?? null,
          show_address_to_public: false,
          is_default: true,
        },
      ];

      setTelehealthLocations(telehealthLocation);
      setInPersonLocations(inPersonLocation);

      postOrchidPro({
        additional_office_locations: [
          ...telehealthLocation,
          ...inPersonLocation,
        ],
      })
        .then((res) => {
          setData(filter_keys(res, PRACTICE_SETTING));
        })
        .catch((err) => {
          console.error(err);
        });
    } else if (data.additional_office_locations) {
      setTelehealthLocations(
        data.additional_office_locations.filter(
          (location) => location.session_location_type === "telehealth",
        ),
      );
      setInPersonLocations(
        data.additional_office_locations.filter(
          (location) => location.session_location_type === "in-person",
        ),
      );
    }
  }, [data]);

  const fieldChangedCallback = useCallback((name, value, multi) => {
    setData((data) =>
      multi
        ? {
            ...data,
            ...multi,
          }
        : {
            ...data,
            [name]: value,
          },
    );
    setChanged(true);
  }, []);

  const onInputChange = (event) => {
    const inputField = event.currentTarget;
    fieldChangedCallback(inputField.name, inputField.value, null);
  };

  const copyAddress = () => {
    const {
      copyAddress,
      office_address_line1,
      office_address_line2,
      office_city,
      office_state,
      office_zipcode,
    } = data;
    const newData = !copyAddress
      ? {
          copyAddress: !copyAddress,
          mail_address_line1: office_address_line1,
          mail_address_line2: office_address_line2,
          mail_city: office_city,
          mail_state: office_state,
          mail_zipcode: office_zipcode,
        }
      : {
          copyAddress: !copyAddress,
        };
    fieldChangedCallback("", "", newData);
  };

  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<OfficeLocation | null>(
    null,
  );
  const [currBillingAddress, setCurrBillingAddress] = useState<{
    office_address_line1: string;
    office_address_line2: string;
    office_city: string;
    office_state: string;
    office_zipcode: string;
  } | null>(null);

  const onDeleteAdditionalLocation = async (officeLocationId) => {
    if (officeLocationId === null) {
      return;
    }

    try {
      const nextOrchidPro = await postOrchidPro({
        additional_office_locations: data.additional_office_locations.filter(
          (location) => location.office_location_id !== officeLocationId,
        ),
      });
      setData(filter_keys(nextOrchidPro, PRACTICE_SETTING));
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddLocation = () => {
    setCurrentLocation(null);
    setEditMode(false);
    setShowLocationEditor(true);
    setCurrBillingAddress(null);
    setShowBillingAddressEditor(false);
  };

  const handleEditLocation = (location) => {
    setCurrentLocation(location);
    setEditMode(true);
    setShowLocationEditor(true);
    setCurrBillingAddress(null);
    setShowBillingAddressEditor(false);
  };

  const [showBillingAddressEditor, setShowBillingAddressEditor] =
    useState(false);

  const editBillingAddress = () => {
    setCurrentLocation(null);
    setEditMode(false);
    setShowLocationEditor(false);
    setShowBillingAddressEditor(true);
    const {
      office_address_line1,
      office_address_line2,
      office_city,
      office_state,
      office_zipcode,
    } = data;
    setCurrBillingAddress({
      office_address_line1,
      office_address_line2,
      office_city,
      office_state,
      office_zipcode,
    });
  };

  const [validated, setValidated] = useState(false);

  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();
  const [changed, setChanged] = useState<boolean>();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      setValidated(true);
      setError("Please fill in required fields correctly.");
      scrollToInvalidInput(form);
      return;
    }
    try {
      await postOrchidPro(data);
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setChanged(false);
    }
  };

  return (
    <>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
          className={styles["account-setting__error-message"]}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      {showLocationEditor && (
        <OfficeLocationEditor
          show={showLocationEditor}
          onHide={() => setShowLocationEditor(false)}
          editMode={editMode}
          locationItem={currentLocation}
          amplitudePageName={amplitudePageName}
          setData={setData}
          additionalLocations={data.additional_office_locations}
        />
      )}
      {showBillingAddressEditor && (
        <BillingAddressEditor
          show={showBillingAddressEditor}
          onHide={() => setShowBillingAddressEditor(false)}
          amplitudePageName={amplitudePageName}
          setData={setData}
          currBillingAddress={currBillingAddress}
        />
      )}
      <div className={styles["practice-setting__body"]}>
        <Form
          noValidate
          name="practiceSetting"
          validated={validated}
          onSubmit={handleSubmit}
          id="practiceSetting"
        >
          <div className={styles["practice-details__card"]}>
            <div className={styles["practice-details__col"]}>
              <div className={styles["practice-details__title"]}>
                Practice Settings
              </div>
              {PRACTICE_SETTING_RADIOS.map((radio, index) => {
                return (
                  <RadioButton
                    key={"practice-setting" + index}
                    className={styles["practice-details__radio"]}
                    id={radio.value}
                    label={radio.label}
                    value={radio.value}
                    name="org_setting"
                    onChange={onInputChange}
                    checked={data.org_setting === radio.value}
                    onClick={() =>
                      track(
                        amplitudePageName,
                        "practice-settings-radio-button",
                        "click",
                      )
                    }
                  />
                );
              })}
            </div>
          </div>
          <div className={styles["practice-details__card"]}>
            <div className={styles["practice-details__col"]}>
              <div className={styles["practice-details__title"]}>
                Practice Information
              </div>
              <Form.Group>
                <Form.Label
                  className={getClasses(
                    styles["practice-details__form-label"],
                    "d-flex align-items-center",
                  )}
                >
                  Business Name{" "}
                  <OverlayTrigger
                    placement="right"
                    trigger={["hover", "click"]}
                    overlay={
                      <Tooltip id="business-name-tooltip">
                        You can leave this blank if you have not yet created a
                        separate business for your practice.
                      </Tooltip>
                    }
                  >
                    <OutlineInfoCircle16 className={"blue ml-1"} />
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="business_entity_name"
                  value={data.business_entity_name || ""}
                  onChange={onInputChange}
                  placeholder={"Enter Business Name"}
                  onFocus={() =>
                    track(amplitudePageName, "business-name-field", "focus")
                  }
                />
              </Form.Group>
              <Form.Group>
                <Form.Label
                  className={getClasses(
                    styles["practice-details__form-label"],
                    "d-flex align-items-center",
                  )}
                >
                  Business Entity Type{" "}
                  <OverlayTrigger
                    placement="right"
                    trigger={["hover", "click"]}
                    overlay={
                      <Tooltip id="business-entity-type-tooltip">
                        You can leave this blank if you have not yet created a
                        separate business entity type for your practice.
                      </Tooltip>
                    }
                  >
                    <OutlineInfoCircle16 className={"blue ml-1"} />
                  </OverlayTrigger>
                </Form.Label>
                {data.org_setting === "solo practitioner" ? (
                  <CustomSelect
                    isMulti={false}
                    value={
                      ORG_ENTITY_TYPE_SOLO.find(
                        (option) => option.value === data.org_entity_type,
                      ) || ""
                    }
                    placeholder={"Select Businesss Entity Type"}
                    onChange={(el) =>
                      fieldChangedCallback("", "", {
                        org_entity_type: el.value,
                        organization_national_provider_identifier: null,
                      })
                    }
                    options={ORG_ENTITY_TYPE_SOLO}
                    key={`org-entity__${data.org_setting}`}
                    onClick={() =>
                      track(
                        amplitudePageName,
                        "business-entity-type-dropdown",
                        "click",
                      )
                    }
                  />
                ) : (
                  <CustomSelect
                    isMulti={false}
                    value={
                      ORG_ENTITY_TYPE_GROUP.find(
                        (option) => option.value === data.org_entity_type,
                      ) || ""
                    }
                    placeholder={"Select Businesss Entity Type"}
                    onChange={(el) => {
                      fieldChangedCallback("org_entity_type", el.value, null);
                    }}
                    options={ORG_ENTITY_TYPE_GROUP}
                    key={`org-entity__${data.org_setting}`}
                    onClick={() =>
                      track(
                        amplitudePageName,
                        "business-entity-type-dropdown",
                        "click",
                      )
                    }
                  />
                )}
              </Form.Group>
              {data.org_setting === "solo practitioner" &&
              data.org_entity_type === "sole proprietor" ? null : (
                <Form.Group>
                  <Form.Label
                    className={styles["practice-details__form-label"]}
                  >
                    Organization NPI
                  </Form.Label>
                  <Form.Control
                    maxLength={10}
                    minLength={10}
                    type="text"
                    name="organization_national_provider_identifier"
                    value={data.organization_national_provider_identifier || ""}
                    onChange={onInputChange}
                    placeholder={"Enter Organization NPI"}
                    onFocus={() =>
                      track(
                        amplitudePageName,
                        "organization-npi-field",
                        "focus",
                      )
                    }
                  />
                </Form.Group>
              )}
              <Form.Group>
                <Form.Label className={styles["practice-details__form-label"]}>
                  Business EIN/Tax ID
                </Form.Label>
                <Form.Control
                  type="text"
                  name="tax_id"
                  value={data.tax_id || ""}
                  onChange={onInputChange}
                  placeholder={"Enter Tax ID"}
                  onFocus={() =>
                    track(amplitudePageName, "business-ein-tax-field", "focus")
                  }
                />
              </Form.Group>
            </div>
            <hr className={styles["practice-details__hr"]} />
            <div className={styles["practice-details__col"]}>
              <Form.Group>
                <Form.Label className={styles["practice-details__form-label"]}>
                  Primary Phone Number
                </Form.Label>
                <FormControlWithMask
                  name="phone_number"
                  mask={PHONE_NUMBER_MASK}
                  maskChar="-"
                  maskRegex="\d"
                  value={data.phone_number || ""}
                  onChange={onInputChange}
                  onFocus={() => {
                    track(amplitudePageName, "business-number-field", "focus");
                  }}
                />
              </Form.Group>
              {/* <Form.Group>
                <Form.Label className={styles["practice-details__form-label"]}>
                  Cell Number
                </Form.Label>
                <FormControlWithMask
                  name="cell_number"
                  mask={PHONE_NUMBER_MASK}
                  maskChar="-"
                  maskRegex="\d"
                  value={data.cell_number || ""}
                  onChange={onInputChange}
                  disabled
                />
              </Form.Group> */}
            </div>
            <p
              className={getClasses(
                "gray01 mb-0",
                styles["practice-details__text"],
              )}
            >
              Only the primary phone number will be used for notifications.
            </p>
          </div>

          <div className={styles["practice-details__card"]}>
            <div className={styles["practice-details__title"]}>
              Practice Logo
            </div>
            <p className={styles["practice-details__text"]}>
              Add your practice's logo for use on your Orchid documents, such as
              Invoices, Receipts and Superbill. Upload a .jpg or .png image
              under 10 MB, with a minimum of 200px height and 300px width.
            </p>

            <ImageUpload />
          </div>

          <div className={styles["practice-details__card"]}>
            <div className={styles["practice-details__title"]}>
              Office Location
            </div>
            <p className={styles["practice-details__text"]}>
              Each office address is hidden from public view by default.
              However, office addresses are always visible to clients who sign
              in to the Client Portal or receive appointment reminders. When
              creating a session, you can set a default office location to be
              auto-selected.
              {/* Once a location is created, it cannot be deleted—only deactivated—due to superbill and invoicing requirements. */}
            </p>

            <div className={styles["practice-details__table-title"]}>
              Telehealth Location
            </div>
            <Table className={styles["office-location__table"]}>
              <thead>
                <tr>
                  <th className={styles["office-location__office-name"]}>
                    Office Name
                  </th>
                  <th className={styles["office-location__location"]}>
                    Location
                  </th>
                  <th className={styles["office-location__place-of-service"]}>
                    Place of Service
                  </th>
                  <th
                    className={styles["office-location__telehealth-modifier"]}
                  >
                    Telehealth Modifier
                  </th>
                  <th className={styles["office-location__public-view"]}>
                    Public View
                  </th>
                  <th className={styles["office-location__tag"]}>Tag</th>
                  <th className={styles["office-location__actions"]}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {telehealthLocations.map((el, index) => {
                  return (
                    <tr key={index}>
                      <td className={styles["office-location__office-name"]}>
                        {el.office_name}
                      </td>

                      <td className={styles["office-location__location"]}>
                        {el.office_address_line1 ? (
                          <>
                            {el.office_address_line1}
                            {el.office_address_line2
                              ? `, ${el.office_address_line2}`
                              : ""}
                            <br />
                            {el.office_city}, {el.office_state}{" "}
                            {el.office_zipcode}
                          </>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td
                        className={styles["office-location__place-of-service"]}
                      >
                        {TELEHEALTH_POS_OPTIONS.map(
                          (option) =>
                            option.value === el.telehealth_pos_code &&
                            option.label,
                        )}
                      </td>
                      <td
                        className={
                          styles["office-location__telehealth-modifier"]
                        }
                      >
                        {el.telehealth_modifier || "-"}
                      </td>
                      <td className={styles["office-location__public-view"]}>
                        {el.show_address_to_public ? "Shown" : "Hidden"}
                      </td>
                      <td className={styles["office-location__tag"]}>
                        {el.is_default && (
                          <span
                            className={styles["office-location__badge-primary"]}
                          >
                            Default
                          </span>
                        )}
                      </td>
                      <td className={styles["office-location__actions"]}>
                        <ActionsDropdown>
                          <Dropdown.Item onClick={() => handleEditLocation(el)}>
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              onDeleteAdditionalLocation(el.office_location_id)
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </ActionsDropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Button
              disabled
              variant="link"
              className="mt-3 mb-4 p-0"
              onClick={handleAddLocation}
            >
              + Add Telehealth Location
            </Button>

            <div className={styles["practice-details__table-title"]}>
              In-person Location
            </div>
            <Table className={styles["office-location__table"]}>
              <thead>
                <tr>
                  <th className={styles["office-location__office-name"]}>
                    Office Name
                  </th>
                  <th className={styles["office-location__location"]}>
                    Location
                  </th>
                  <th className={styles["office-location__place-of-service"]}>
                    Place of Service
                  </th>
                  <th
                    className={styles["office-location__telehealth-modifier"]}
                  >
                    Telehealth Modifier
                  </th>
                  <th className={styles["office-location__public-view"]}>
                    Public View
                  </th>
                  <th className={styles["office-location__tag"]}>Tag</th>
                  <th className={styles["office-location__actions"]}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {inPersonLocations.map((el) => {
                  const realIndex = inPersonLocations.indexOf(el);
                  return (
                    <tr key={realIndex}>
                      <td className={styles["office-location__office-name"]}>
                        {el.office_name || "-"}
                      </td>

                      <td className={styles["office-location__location"]}>
                        {el.office_address_line1}
                        {el.office_address_line2
                          ? `, ${el.office_address_line2}`
                          : ""}
                        <br />
                        {el.office_city}, {el.office_state} {el.office_zipcode}
                      </td>
                      <td
                        className={styles["office-location__place-of-service"]}
                      >
                        {IN_PERSON_POS_OPTIONS.map(
                          (option) =>
                            option.value === el.in_person_pos_code &&
                            option.label,
                        )}
                      </td>
                      <td
                        className={
                          styles["office-location__telehealth-modifier"]
                        }
                      >
                        {el.in_person_modifier || "-"}
                      </td>
                      <td className={styles["office-location__public-view"]}>
                        {el.show_address_to_public ? "Shown" : "Hidden"}
                      </td>
                      <td className={styles["office-location__tag"]}>
                        {el.is_default && (
                          <span
                            className={styles["office-location__badge-primary"]}
                          >
                            Default
                          </span>
                        )}
                      </td>
                      <td className={styles["office-location__actions"]}>
                        <ActionsDropdown>
                          <Dropdown.Item
                            onClick={() => {
                              handleEditLocation(el);
                            }}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              onDeleteAdditionalLocation(el.office_location_id)
                            }
                          >
                            Delete
                          </Dropdown.Item>
                        </ActionsDropdown>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Button
              disabled
              variant="link"
              className="mt-3 p-0"
              onClick={handleAddLocation}
            >
              + Add In-person Location
            </Button>
          </div>

          <div className={styles["practice-details__card"]}>
            <div className={styles["practice-details__title"]}>
              Billing Address
            </div>
            <p className={styles["practice-details__text"]}>
              Your business billing address appears on subscription invoices, as
              well as client-facing billing documents like invoices, statements,
              and superbills.
            </p>

            <Table className={styles["office-location__table"]}>
              <thead>
                <tr>
                  <th className={styles["office-location__office-name"]}>
                    Name
                  </th>

                  <th className={styles["office-location__location"]}>
                    Office /Address
                  </th>
                  <th
                    className={styles["office-location__place-of-service"]}
                  ></th>
                  <th
                    className={styles["office-location__telehealth-modifier"]}
                  ></th>
                  <th className={styles["office-location__public-view"]}></th>
                  <th className={styles["office-location__tag"]}></th>
                  <th className={styles["office-location__actions"]}></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={styles["office-location__office-name"]}>
                    <b>Business Billing</b>
                  </td>

                  <td className={styles["office-location__location"]}>
                    {data.office_address_line1 ? (
                      <>
                        {data.office_address_line1}
                        {data.office_address_line2
                          ? `, ${data.office_address_line2}`
                          : ""}
                        <br />
                        {data.office_city}, {data.office_state}{" "}
                        {data.office_zipcode}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td
                    className={styles["office-location__place-of-service"]}
                  ></td>
                  <td
                    className={styles["office-location__telehealth-modifier"]}
                  ></td>
                  <td className={styles["office-location__public-view"]}></td>
                  <td className={styles["office-location__tag"]}></td>
                  <td className={styles["office-location__actions"]}>
                    <IconButton onClick={editBillingAddress}>
                      <OutlineEdit1 />
                    </IconButton>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
          <div className={styles["practice-details__card"]}>
            <div className={styles["practice-details__col"]}>
              <>
                {/* <div
              className={styles["practice-details__title"]}
            >
              Business Address
            </div>

            <Form.Group>
              <Form.Label className={styles["practice-details__form-label"]}>
                Street Address <span className="required">*</span>
              </Form.Label>
              <Form.Control
                required
                name="office_address_line1"
                value={data.office_address_line1 || ""}
                onChange={onInputChange}
                placeholder={"Enter Address"}
                onFocus={() =>
                  track(
                    amplitudePageName,
                    "business-street-address-field",
                    "focus",
                  )
                }
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className={styles["practice-details__form-label"]}>
                City <span className="required">*</span>
              </Form.Label>
              <Form.Control
                required
                name="office_city"
                onChange={onInputChange}
                value={data.office_city || ""}
                placeholder={"Enter City"}
                onFocus={() =>
                  track(amplitudePageName, "business-city-field", "focus")
                }
              />
            </Form.Group>

            <Row>
              <Form.Group as={Col} className={"mb-0"}>
                <Form.Label className={styles["practice-details__form-label"]}>
                  State <span className="required">*</span>
                </Form.Label>

                <CustomSelect
                  required
                  isMulti={false}
                  value={
                    STATE_OPTIONS.find(
                      (option) => option.value === data.office_state,
                    ) || ""
                  }
                  maxMenuHeight={200}
                  onChange={(el) => {
                    track(
                      amplitudePageName,
                      "business-state-dropdown",
                      "click",
                    );
                    fieldChangedCallback("office_state", el.value);
                  }}
                  options={STATE_OPTIONS}
                />
              </Form.Group>

              <Form.Group as={Col} className={"mb-0"}>
                <Form.Label className={styles["practice-details__form-label"]}>
                  Zipcode <span className="required">*</span>
                </Form.Label>
                <Form.Control
                  required
                  name="office_zipcode"
                  value={data.office_zipcode || ""}
                  onChange={onInputChange}
                  placeholder={"Enter Zipcode"}
                  onFocus={() =>
                    track(amplitudePageName, "business-zip-field", "focus")
                  }
                />
              </Form.Group>
            </Row> */}

                <div className={styles["practice-details__title"]}>
                  Home Address
                </div>

                <Checkbox
                  variant={"rectangle"}
                  label={
                    <label className="rg-12 mb-3">
                      Same as Business Address
                    </label>
                  }
                  onChange={copyAddress}
                  checked={data?.copyAddress || false}
                />
                <Form.Group>
                  <Form.Label
                    className={styles["practice-details__form-label"]}
                  >
                    Street Address <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    name="mail_address_line1"
                    required
                    value={data.mail_address_line1 || ""}
                    onChange={onInputChange}
                    placeholder={"Enter Address"}
                    onFocus={() =>
                      track(
                        amplitudePageName,
                        "home-street-address-field",
                        "focus",
                      )
                    }
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label
                    className={styles["practice-details__form-label"]}
                  >
                    City <span className="required">*</span>
                  </Form.Label>
                  <Form.Control
                    name="mail_city"
                    required
                    onChange={onInputChange}
                    value={data.mail_city || ""}
                    placeholder={"Enter City"}
                    onFocus={() =>
                      track(amplitudePageName, "home-city-field", "focus")
                    }
                  />
                </Form.Group>

                <Row>
                  <Form.Group as={Col} className={"mb-0"}>
                    <Form.Label
                      className={styles["practice-details__form-label"]}
                    >
                      State <span className="required">*</span>
                    </Form.Label>
                    <CustomSelect
                      required
                      isMulti={false}
                      value={
                        STATE_OPTIONS.find(
                          (option) => option.value === data.mail_state,
                        ) || ""
                      }
                      onChange={(el) => {
                        track(amplitudePageName, "home-state-field", "click");
                        fieldChangedCallback("mail_state", el.value, null);
                      }}
                      options={STATE_OPTIONS}
                    />
                  </Form.Group>

                  <Form.Group as={Col} className={"mb-0"}>
                    <Form.Label
                      className={styles["practice-details__form-label"]}
                    >
                      Zipcode <span className="required">*</span>
                    </Form.Label>
                    <Form.Control
                      name="mail_zipcode"
                      value={data.mail_zipcode || ""}
                      required
                      onChange={onInputChange}
                      placeholder={"Enter Zipcode"}
                      onFocus={() =>
                        track(amplitudePageName, "home-zip-field", "focus")
                      }
                    />
                  </Form.Group>
                </Row>
              </>
            </div>
          </div>
        </Form>
      </div>
      <div className={styles["practice-setting__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          type="submit"
          className={styles["practice-setting__save-button"]}
          disabled={!changed}
          form={"practiceSetting"}
          onClick={() => track(amplitudePageName, "save-button", "click")}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default PracticeDetails;
